import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Badge, Card, Skeleton, MotionNumber, SlideToConfirm } from '../ui';
import { formatResumeTime, formatUsdSigned } from '../../lib/format';
import { tg } from '../../lib/tg';
import type {
  BalancesChainBalance,
  BotState,
  BotTogglePayload,
  MyBalancesResponse,
  PositionsResponse,
  UserProfile,
} from '../../lib/types';

interface Props {
  me?: UserProfile | null;
  /** Architecture-C three-tile balances (main wallet + PM/HL bot
   *  bankrolls). Optional — when absent (loading, fetch errored) the
   *  hero renders zeros so the layout doesn't collapse. */
  balances?: MyBalancesResponse | null;
  positions?: PositionsResponse;
  pnlToday: number;
  isLoading: boolean;
  toggleBusy: boolean;
  /** Flip one bot at a time. Payload carries exactly the bot the user
   *  tapped — the API leaves the other untouched. */
  onToggleBot: (patch: BotTogglePayload) => void;
}

type BotStatus = 'working' | 'paused' | 'idle';

// Stablecoins folded into a chain's user-facing USD figure. POL/ETH gas
// is excluded — same set BalancesCard uses for the Wallet page tiles.
const STABLE_SYMBOLS = new Set(['USDC', 'USDC.e', 'USDT', 'pUSD']);

function sumStable(chain: BalancesChainBalance | undefined): number {
  if (!chain) return 0;
  return chain.tokens
    .filter((tok) => STABLE_SYMBOLS.has(tok.symbol))
    .reduce((acc, tok) => acc + Number(tok.amount || 0), 0);
}

export function HeroBalance({
  me,
  balances,
  positions,
  pnlToday,
  isLoading,
  toggleBusy,
  onToggleBot,
}: Props) {
  const { t } = useTranslation();

  const mainWalletUsd =
    sumStable(balances?.main_wallet.polygon) +
    sumStable(balances?.main_wallet.arbitrum);
  // PM bot bankroll = pUSD on the POLY_1271 deposit wallet. HL bot
  // bankroll = Hyperliquid account_value (margin + spot USDC).
  const pmBotUsd = Number(balances?.pm_bot_active.amount ?? 0);
  const hlBotUsd = Number(balances?.hl_bot_active.amount ?? 0);
  const totalUsd = mainWalletUsd + pmBotUsd + hlBotUsd;

  // Net of platform fees — what the user actually kept.
  const totalPnl = Number(
    positions?.total_net_pnl ?? positions?.total_realized_pnl ?? 0,
  );
  const openCount = positions?.open_count ?? 0;
  const bots = me?.bots;
  const pmState: BotState | undefined = bots?.pm;
  const hlState: BotState | undefined = bots?.hl;
  // Legacy `bot_enabled` is the OR-fold; OR over per-bot enabled gives
  // the same answer but reads off the new field so the next consumer
  // can drop the legacy fallback when it's gone.
  const anyBotEnabled =
    (pmState?.enabled ?? false) || (hlState?.enabled ?? false);
  const hasFunds = totalUsd > 0 || openCount > 0;
  const status: BotStatus = !anyBotEnabled
    ? 'paused'
    : hasFunds
      ? 'working'
      : 'idle';

  return (
    <Card pad="lg" variant="paper" className="pb-grain overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <span className="pb-eyebrow">{t('dashboard.balance')}</span>
        <BotStatusPill status={status} />
      </div>

      {isLoading ? (
        <Skeleton className="mt-3 h-[44px] w-40" />
      ) : (
        <div className="pb-numeral-lg mt-2 text-[44px] leading-none text-tg-text">
          <span className="mr-1 align-top text-[22px] text-tg-hint">$</span>
          <MotionNumber value={totalUsd} format="number" />
        </div>
      )}

      <dl className="mt-4 grid grid-cols-3 gap-x-3 gap-y-2 text-[12px]">
        <Stat
          label={t('dashboard.cap_main')}
          value={<MotionNumber value={mainWalletUsd} format="usd" />}
        />
        <Stat
          label={t('dashboard.cap_pm')}
          value={<MotionNumber value={pmBotUsd} format="usd" />}
        />
        <Stat
          label={t('dashboard.cap_hl')}
          value={<MotionNumber value={hlBotUsd} format="usd" />}
        />
        <Stat
          label={t('dashboard.pnl_today')}
          value={
            <span
              className={clsx(
                pnlToday > 0 && 'text-pb-success',
                pnlToday < 0 && 'text-pb-danger',
              )}
            >
              {formatUsdSigned(pnlToday)}
            </span>
          }
        />
        <Stat
          label={t('dashboard.pnl_total')}
          value={
            <span
              className={clsx(
                totalPnl > 0 && 'text-pb-success',
                totalPnl < 0 && 'text-pb-danger',
              )}
            >
              {formatUsdSigned(totalPnl)}
            </span>
          }
        />
        <Stat label={t('dashboard.cap_open')} value={String(openCount)} />
      </dl>

      {pmState?.available || hlState?.available ? (
        <div className="mt-4 flex flex-col gap-2.5">
          {pmState?.available ? (
            <BotSwitch
              label={t('dashboard.cap_pm')}
              state={pmState}
              busy={toggleBusy}
              onToggle={(enabled) => {
                tg.hapticSuccess();
                onToggleBot({ pm: enabled });
              }}
            />
          ) : null}
          {hlState?.available ? (
            <BotSwitch
              label={t('dashboard.cap_hl')}
              state={hlState}
              busy={toggleBusy}
              onToggle={(enabled) => {
                tg.hapticSuccess();
                onToggleBot({ hl: enabled });
              }}
            />
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function BotSwitch({
  label,
  state,
  busy,
  onToggle,
}: {
  label: string;
  state: BotState;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const adminLocked = state.admin_paused;
  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';
  const resumeAt = state.pause?.resume_at
    ? formatResumeTime(state.pause.resume_at, locale)
    : '';
  // Sub-label is the most informative status to surface inline:
  //   - an auto (circuit-breaker) pause names its resume time
  //   - then admin override (user can't flip past it)
  //   - then the user's own on/off
  const sub = resumeAt
    ? t('dashboard.bot_paused_auto', { time: resumeAt })
    : adminLocked
      ? t('dashboard.bot_admin_paused')
      : state.enabled
        ? t('dashboard.bot_working')
        : t('dashboard.bot_paused');
  return (
    <div className="rounded-pb-sm border border-pb-border-soft bg-pb-paper px-3 py-2.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="pb-eyebrow !text-[10px] !tracking-[0.07em]">
          {label}
        </span>
        <span className="text-[12px] leading-tight text-tg-hint">{sub}</span>
      </div>
      <SlideToConfirm
        on={state.enabled}
        busy={busy}
        disabled={adminLocked}
        ariaLabel={`${label}: ${sub}`}
        labelOff={t('dashboard.slide_to_enable')}
        labelOn={t('dashboard.slide_to_disable')}
        onConfirm={onToggle}
      />
    </div>
  );
}

function BotStatusPill({ status }: { status: BotStatus }) {
  const { t } = useTranslation();
  const tone =
    status === 'working' ? 'success' : status === 'paused' ? 'warn' : 'neutral';
  const label =
    status === 'working'
      ? t('dashboard.bot_working')
      : status === 'paused'
        ? t('dashboard.bot_paused')
        : t('dashboard.bot_idle');
  return (
    <Badge tone={tone} dot>
      {label}
    </Badge>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="pb-eyebrow !text-[10px] !tracking-[0.07em]">{label}</dt>
      <dd className="pb-numeral mt-0.5 text-[15px] text-tg-text">{value}</dd>
    </div>
  );
}
